/**
 * HistoricalPage - Historical Electoral Trends Analysis
 *
 * Main page for viewing historical electoral data with:
 * - Municipality selection
 * - Election type filter
 * - Animated bar chart with year slider
 * - Line chart for trend analysis
 *
 * Author: Claude Code
 * Date: 2025-12-01
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { findMunicipality } from '../../services/api';
import { MunicipalityResult } from '../../types';
import HistoricalChart from './HistoricalChart';
import NoTranslate from '../common/NoTranslate';

// Election type definitions with available years
const ELECTION_TYPES = [
  { value: 'komunalni', label: 'Komunální volby', years: [2022, 2018, 2014, 2010, 2006] },
  { value: 'senatni', label: 'Senátní volby', years: [2024, 2022, 2020, 2018, 2016, 2014] },
  { value: 'krajske', label: 'Krajské volby', years: [2024, 2020, 2016, 2012] },
  { value: 'snemovni', label: 'Sněmovní volby', years: [2021, 2017, 2013] },
  { value: 'prezidentske', label: 'Prezidentské volby', years: [2023, 2018, 2013] },
  { value: 'evropske', label: 'Evropské volby', years: [2024, 2019, 2014] },
];

const HistoricalPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  // Filter state
  const [electionType, setElectionType] = useState<string>('komunalni');
  const [municipalityQuery, setMunicipalityQuery] = useState<string>('');
  const [municipalityCode, setMunicipalityCode] = useState<string>('');
  const [municipalityName, setMunicipalityName] = useState<string>('');
  const [chartType, setChartType] = useState<'animation' | 'lines'>('lines');
  const [topN, setTopN] = useState<number>(6);

  // Dropdown data
  const [municipalities, setMunicipalities] = useState<MunicipalityResult[]>([]);
  const [showMunicipalityDropdown, setShowMunicipalityDropdown] = useState<boolean>(false);
  const [municipalityLoading, setMunicipalityLoading] = useState<boolean>(false);

  // Get available years for selected election type
  const availableYears = ELECTION_TYPES.find(e => e.value === electionType)?.years || [];

  // Debounced municipality search
  useEffect(() => {
    const searchMunicipalities = async () => {
      if (municipalityQuery.length < 2) {
        setMunicipalities([]);
        return;
      }

      setMunicipalityLoading(true);
      try {
        const results = await findMunicipality(municipalityQuery, 30);
        setMunicipalities(results);
        setShowMunicipalityDropdown(true);
      } catch (error) {
        console.error('Municipality search error:', error);
        setMunicipalities([]);
      } finally {
        setMunicipalityLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchMunicipalities, 300);
    return () => clearTimeout(debounceTimer);
  }, [municipalityQuery]);

  // Handle municipality selection
  const handleMunicipalitySelect = useCallback((municipality: MunicipalityResult) => {
    setMunicipalityCode(municipality.code);
    setMunicipalityName(municipality.name);
    setMunicipalityQuery(municipality.name);
    setShowMunicipalityDropdown(false);
  }, []);

  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  // Reset municipality when election type changes
  useEffect(() => {
    setMunicipalityCode('');
    setMunicipalityName('');
    setMunicipalityQuery('');
  }, [electionType]);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-purple-800 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <svg className="w-8 h-8 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
            <div>
              <h1 className="text-xl font-bold">Phoenix Electoral Analytics</h1>
              <p className="text-sm text-purple-200">Historické trendy v6.1</p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <span className="text-sm">{user?.email}</span>
            <button
              onClick={handleSignOut}
              className="px-3 py-1 text-sm bg-red-500 hover:bg-red-600 rounded"
            >
              Odhlásit
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Page Title */}
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Historické volební trendy</h2>
          <p className="text-gray-600">
            Sledujte vývoj volebních preferencí v čase. Vyberte obec a zobrazte trendy napříč volebními roky.
          </p>
        </div>

        {/* Filter Panel */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Nastavení zobrazení
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Election Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Typ voleb
              </label>
              <select
                value={electionType}
                onChange={(e) => setElectionType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
              >
                {ELECTION_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Municipality Search */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Obec / Okres
              </label>
              <input
                type="text"
                value={municipalityQuery}
                onChange={(e) => {
                  setMunicipalityQuery(e.target.value);
                  setMunicipalityCode('');
                  setMunicipalityName('');
                }}
                onFocus={() => municipalityQuery.length >= 2 && setShowMunicipalityDropdown(true)}
                placeholder="Začněte psát název..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
              />
              {municipalityLoading && (
                <div className="absolute right-3 top-9">
                  <svg className="animate-spin h-5 w-5 text-purple-500" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              )}

              {/* Municipality Dropdown */}
              {showMunicipalityDropdown && municipalities.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                  {municipalities.map((m) => (
                    <button
                      key={m.code}
                      onClick={() => handleMunicipalitySelect(m)}
                      className="w-full text-left px-4 py-2 hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                    >
                      <NoTranslate as="div" className="font-medium text-gray-800">{m.name}</NoTranslate>
                      <NoTranslate as="div" className="text-sm text-gray-500">
                        {m.district} • Kód: {m.code}
                      </NoTranslate>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Chart Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Typ grafu
              </label>
              <select
                value={chartType}
                onChange={(e) => setChartType(e.target.value as 'animation' | 'lines')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="lines">Liniový graf (trendy)</option>
                <option value="animation">Animovaný sloupcový (slider)</option>
              </select>
            </div>

            {/* Top N Parties */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Počet stran
              </label>
              <select
                value={topN}
                onChange={(e) => setTopN(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-purple-500 focus:border-purple-500"
              >
                <option value={4}>Top 4</option>
                <option value={6}>Top 6</option>
                <option value={8}>Top 8</option>
                <option value={10}>Top 10</option>
              </select>
            </div>
          </div>

          {/* Selected Municipality Info */}
          {municipalityCode && (
            <div className="mt-4 p-3 bg-purple-50 rounded-md">
              <div className="flex items-center justify-between">
                <div>
                  <span className="font-medium text-purple-800">Vybráno:</span>
                  <NoTranslate as="span" className="ml-2 text-purple-700">{municipalityName}</NoTranslate>
                  <span className="ml-2 text-purple-500 text-sm">(kód: {municipalityCode})</span>
                </div>
                <button
                  onClick={() => {
                    setMunicipalityCode('');
                    setMunicipalityName('');
                    setMunicipalityQuery('');
                  }}
                  className="text-purple-600 hover:text-purple-800"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Available Years Info */}
          <div className="mt-4 text-sm text-gray-500">
            <span className="font-medium">Dostupné roky:</span>{' '}
            {availableYears.join(', ')}
          </div>
        </div>

        {/* Chart Section */}
        {municipalityCode ? (
          <HistoricalChart
            municipalityCode={municipalityCode}
            electionType={electionType}
            chartType={chartType}
            topN={topN}
          />
        ) : (
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-700 mb-2">Vyberte obec</h3>
              <p className="text-gray-500">
                Pro zobrazení historických trendů nejprve vyberte obec nebo okres pomocí vyhledávání výše.
              </p>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Jak číst grafy?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4" />
                </svg>
              </div>
              <div className="ml-4">
                <h4 className="font-medium text-gray-800">Liniový graf</h4>
                <p className="text-sm text-gray-600">
                  Zobrazuje vývoj volebních preferencí jednotlivých stran v čase.
                  Každá linie představuje jednu stranu a její úspěšnost napříč volbami.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <h4 className="font-medium text-gray-800">Animovaný sloupcový graf</h4>
                <p className="text-sm text-gray-600">
                  Interaktivní vizualizace s posuvníkem roku. Můžete přehrát animaci
                  nebo ručně vybrat konkrétní rok pomocí slideru.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-400 py-6 mt-12">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm">
          <p>Phoenix Electoral Analytics v6.1 | Data: ČSÚ (Český statistický úřad)</p>
          <p className="mt-1">© 2025 Milan Pecka</p>
        </div>
      </footer>
    </div>
  );
};

export default HistoricalPage;
